import { getPages } from 'src/components/pipelines/utils/pagination';
import { useQuery } from 'src/utils/fb-query';

import { getMergeQueuePullRequestsUrl } from '../constants';
import {
  MergeQueuePullRequestsParams,
  MergeQueuePullRequestsResponse,
  UseQueuedPullRequestsParams,
  UseQueuedPullRequestsResult,
} from '../types';
import { MERGE_QUEUES_PR_PAGE_LENGTH } from '../utils';

export const fetchMergeQueuePullRequests = async ({
  mergeQueueUuid,
  owner,
  slug,
  page = '1',
}: MergeQueuePullRequestsParams): Promise<MergeQueuePullRequestsResponse> => {
  const url = getMergeQueuePullRequestsUrl(owner, slug, mergeQueueUuid, {
    page,
    pagelen: MERGE_QUEUES_PR_PAGE_LENGTH.toString(),
  });

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(
      `Failed to fetch merge queue pull requests: ${response.status} ${response.statusText}`
    );
  }

  return response.json();
};

export const useQueuedPullRequests = ({
  mergeQueueUuid,
  page,
  owner,
  slug,
}: UseQueuedPullRequestsParams): UseQueuedPullRequestsResult => {
  const {
    data: mergeQueuePullRequests,
    isPending: isMergeQueuePullRequestsPending,
  } = useQuery(fetchMergeQueuePullRequests, {
    mergeQueueUuid,
    owner,
    slug,
    page,
  });

  if (!mergeQueueUuid || !owner || !slug || isMergeQueuePullRequestsPending) {
    return {
      mergeQueuePullRequests: [],
      queuedPullRequestsPages: [],
      isQueuedPullRequestsLoading: isMergeQueuePullRequestsPending,
    };
  }

  const queuedPullRequestsPages = getPages(
    mergeQueuePullRequests?.size ?? 0,
    MERGE_QUEUES_PR_PAGE_LENGTH
  );

  return {
    isQueuedPullRequestsLoading: false,
    mergeQueuePullRequests: mergeQueuePullRequests?.values || [],
    queuedPullRequestsPages,
  };
};

export const useIsMergeQueueEmpty = ({
  mergeQueueUuid,
  owner,
  slug,
}: Omit<UseQueuedPullRequestsParams, 'page'>) => {
  const { data: mergeQueuePullRequests, isPending: isMergeQueueEmptyLoading } =
    useQuery(fetchMergeQueuePullRequests, {
      mergeQueueUuid,
      owner,
      slug,
      page: '1',
    });

  if (!mergeQueueUuid || !owner || !slug || isMergeQueueEmptyLoading) {
    return {
      isMergeQueueEmpty: false,
      isMergeQueueEmptyLoading,
    };
  }

  return {
    isMergeQueueEmpty: mergeQueuePullRequests?.size === 0,
    isMergeQueueEmptyLoading,
  };
};
